/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

/**
 * Machine-readable error codes the Teams MiddleTier (AgentAndAppApprovals provider) returns on a
 * failed approve/deny action. Kept in sync with the MiddleTier `AppRequestActionErrorCodes`
 * contract. The extensibility service surfaces this code to the client on `ErrorResponse.ErrorCode`
 * (carried by `ServiceException.response`) so the details view can show a specific, localized
 * message instead of the generic "something went wrong".
 */
export enum ProviderActionErrorCode {
    InvalidAction = 'InvalidAction',
    NotACollabManager = 'NotACollabManager',
    AppNotDelegated = 'AppNotDelegated',
    AlreadyResolved = 'AlreadyResolved',
    DelegationCheckFailed = 'DelegationCheckFailed',
}

/**
 * The relevant, error-shape-agnostic bits pulled off a caught error. `status` is the HTTP status
 * from the failed extensibility call; `errorCode` is the provider error code when present.
 */
export interface ParsedProviderError {
    status?: number;
    errorCode?: string;
}

/**
 * Reads `ErrorResponse.ErrorCode` off an error payload that is either the raw JSON body string
 * (`ServiceException.response`) or an already-parsed object (`ServiceException.result`).
 */
const extractErrorCode = (payload: unknown): string | undefined => {
    if (!payload) {
        return undefined;
    }

    let parsed: unknown = payload;
    if (typeof payload === 'string') {
        try {
            parsed = JSON.parse(payload);
        } catch {
            return undefined;
        }
    }

    if (parsed && typeof parsed === 'object') {
        const code = (parsed as { ErrorCode?: unknown }).ErrorCode;
        if (typeof code === 'string' && code.length > 0) {
            return code;
        }
    }

    return undefined;
};

/**
 * Extracts the HTTP status and provider error code from a caught error. Tolerates any shape
 * (a `ServiceException`, a plain object, or something else entirely) and returns an empty result
 * when nothing useful can be read.
 *
 * @param error The value thrown by a failed extensibility client call.
 */
export const parseProviderError = (error: unknown): ParsedProviderError => {
    const result: ParsedProviderError = {};

    if (!error || typeof error !== 'object') {
        return result;
    }

    const serviceError = error as { status?: unknown; response?: unknown; result?: unknown };

    if (typeof serviceError.status === 'number') {
        result.status = serviceError.status;
    }

    const errorCode = extractErrorCode(serviceError.response) ?? extractErrorCode(serviceError.result);
    if (errorCode) {
        result.errorCode = errorCode;
    }

    return result;
};

/**
 * Maps a failed approve/deny action to a specific, localized message based on the provider
 * `errorCode`, falling back to the generic retry message for any error without a recognized code
 * so unknown failures still get a sensible message.
 *
 * @param error The value thrown by the failed `updateAction` call.
 * @param strings The current locale strings.
 */
export const resolveRespondErrorMessage = (error: unknown, strings: ILocaleKeys): string => {
    const { errorCode } = parseProviderError(error);

    switch (errorCode) {
        case ProviderActionErrorCode.AlreadyResolved:
            return strings.error_respond_already_resolved;
        case ProviderActionErrorCode.AppNotDelegated:
            return strings.error_respond_app_not_delegated;
        case ProviderActionErrorCode.DelegationCheckFailed:
            return strings.error_respond_delegation_check_failed;
        case ProviderActionErrorCode.NotACollabManager:
            return strings.error_respond_not_authorized;
        case ProviderActionErrorCode.InvalidAction:
            return strings.error_respond_invalid_action;
        default:
            break;
    }

    // No specific provider code — fall back to the generic retry message. We intentionally do not
    // map a bare 401/403 to a "not an approver" message: this view serves every approval type, and
    // an unauthenticated/forbidden response can have many causes. The genuine "not an approver" case
    // always arrives with the NotACollabManager provider code handled above.
    return strings.error_respond_failed;
};
